import React, { useCallback, useEffect, useState } from 'react';
import { BodyLarge, BodyRegular } from '@shared/ui/atoms/fonts/Body';
import Colours from '@shared/ui/atoms/Colours';
import styled from "styled-components";
import { UserInfo } from '@shared/models/user.models';
import { useDispatch } from "react-redux";
import { failureNotification } from "@shared/store/notification.duck";
import { agent } from "@shared/services/agent";
import TextField from '@shared/ui/atoms/TextField';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { Row } from '@shared/ui/atoms/StructuralLayout';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

interface MacAddressMapping {
  id: number;
  macAddress: string;
  userId: string;
}

interface Props {
  user: UserInfo;
}

export function UserMacAddressSection(props: Props) {
  const [macAddresses, setMacAddresses] = useState<MacAddressMapping[]>([]);
  const [newMacAddress, setNewMacAddress] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const dispatch = useDispatch();

  const fetchMacAddresses = useCallback(async () => {
    try {
      const data = await agent.requests.get(`/api/user/${props.user.userId}/mac-addresses`);
      setMacAddresses(data);
    } catch {
      // Silently fail on load
    }
  }, [props.user.userId]);

  useEffect(() => {
    fetchMacAddresses();
  }, [fetchMacAddresses]);

  const handleAdd = async () => {
    const trimmed = newMacAddress.trim();
    if (!trimmed) return;

    try {
      setIsSaving(true);
      const saved = await agent.requests.post(`/api/user/${props.user.userId}/mac-addresses`, { macAddress: trimmed });
      setMacAddresses(prev => [...prev, saved]);
      setNewMacAddress('');
    } catch (err: any) {
      const message = err?.response?.body?.message || 'This MAC address is already registered to a user';
      dispatch(failureNotification(message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await agent.requests.del(`/api/user/${props.user.userId}/mac-addresses/${id}`);
      setMacAddresses(prev => prev.filter(m => m.id !== id));
    } catch {
      dispatch(failureNotification('Failed to remove MAC address'));
    }
  };

  return (
    <Wrapper>
      <BodyLarge colour={Colours.blue} style={{marginBottom: 8}}>MAC Addresses</BodyLarge>
      <BodyRegular style={{marginBottom: 16}}>Associate device MAC addresses with this user for automatic office check-in.</BodyRegular>

      {macAddresses.map(mac => (
        <MacRow key={mac.id}>
          <BodyRegular weight={500}>{mac.macAddress}</BodyRegular>
          <Icon icon={IconTypes.Close} color="grey" onClick={() => handleDelete(mac.id)} />
        </MacRow>
      ))}

      <AddRow>
        <TextField value={newMacAddress}
                   onChange={setNewMacAddress}
                   placeholder="textinput.mac-address-placeholder"
                   style={{flex: 1, marginBottom: 0, marginRight: 12}} />
        <PrimaryButton text="Add" click={handleAdd} disabled={isSaving || !newMacAddress.trim()} size={ButtonSize.Small} />
      </AddRow>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  border-bottom: 1px solid ${Colours.mildGrey};
  margin-bottom: 24px;
  padding-bottom: 12px;
`

const MacRow = styled(Row)`
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background-color: ${Colours.veryLightGrey};
  border-radius: 8px;
  margin-bottom: 8px;
`

const AddRow = styled(Row)`
  align-items: flex-end;
  margin-top: 12px;
  margin-bottom: 16px;
`
